import { useEffect, useRef, useCallback } from 'react'
import { usePlaybackStore } from '../store'

export function useFullscreen() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { isFullscreen, setFullscreen } = usePlaybackStore()

  const enterFullscreen = useCallback(async () => {
    const el = containerRef.current
    if (!el) return
    try {
      await el.requestFullscreen()
    } catch {
      // Safari fallback
      const safariEl = el as HTMLDivElement & {
        webkitRequestFullscreen?: () => Promise<void>
      }
      await safariEl.webkitRequestFullscreen?.()
    }
  }, [])

  const exitFullscreen = useCallback(async () => {
    try {
      await document.exitFullscreen()
    } catch {
      const doc = document as Document & {
        webkitExitFullscreen?: () => Promise<void>
      }
      await doc.webkitExitFullscreen?.()
    }
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) exitFullscreen()
    else enterFullscreen()
  }, [isFullscreen, enterFullscreen, exitFullscreen])

  // Sync store with browser fullscreen state
  useEffect(() => {
    const onChange = () => {
      const isFs = !!document.fullscreenElement
      setFullscreen(isFs)
    }

    document.addEventListener('fullscreenchange', onChange)
    document.addEventListener('webkitfullscreenchange', onChange)
    return () => {
      document.removeEventListener('fullscreenchange', onChange)
      document.removeEventListener('webkitfullscreenchange', onChange)
    }
  }, [setFullscreen])

  // React to store changes
  useEffect(() => {
    const isCurrentlyFs = !!document.fullscreenElement
    if (isFullscreen && !isCurrentlyFs) enterFullscreen()
    else if (!isFullscreen && isCurrentlyFs) exitFullscreen()
  }, [isFullscreen, enterFullscreen, exitFullscreen])

  return { containerRef, toggleFullscreen }
}
